<template>
  <div class="container-fluid">
    <h1 class="mb-3">APN Management
      <a href="#apnOverview" data-bs-toggle="collapse">
        <img type="svg" src="@/assets/icons/info.svg" style="width:20px" />
      </a>
    </h1>
    <!-- <img src="..." class="card-img-top" alt="APN diagram"> -->
    <div class="card-body">
      <div id="apnOverview" class="collapse">
        <p class="card-text">
          Viasat has established 2 APNs on the Skylo network/core:
          <b>viasat.poc</b> and <b>viasat.ip</b>. These may act as <em>parent</em>
          APNs (<b>P-APN</b>) for multiple <em>virtual</em> APNs (<b>V-APN</b>).<br>
          Proof-of-Concept customers are each provided with a <em>virtual</em> APN
          to segregate traffic from each other.
          Customers configure the P-APN on their device (using AT commands) and
          the Viasat system will route messages to their V-APN either using the
          <b>NIDD API</b> or forwarding UDP to a customer-specified FQDN and port.
        </p>
        <dl class="row">
          <dt class="col-sm-2">NIDD</dt>
          <dd class="col-sm-10">
            Viasat provides customer with:
            <ul>
              <li><b>P-APN</b> name to configure on device</li>
              <li><b>V-APN</b> name to use for API authentication (identiy)</li>
              <li><b>Viasat API Key</b> to use for API authentication</li>
            </ul>
            Customer uses NIDD API to register with Viasat:
            <ul>
              <li><b>Customer API Key</b> to be used by Viasat callback notifications</li>
              <li><b>Callback URL(s)</b> for each Viasat callback: uplink, downlink delivery, device attach</li>
            </ul>
          </dd>
          <dt class="col-sm-2">UDP</dt>
          <dd class="col-sm-10">
            Viasat provides customer with:
            <ul>
              <li><b>P-APN</b> name to configure on device</li>
              <li><b>V-APN</b> IP address and port (e.g. {{ udpProxyHost }}:{{ udpProxyPort }})</li>
            </ul>
            Customer provides Viasat (via portal or email to Solution Engineer):
            <ul>
              <li><b>UDP Server</b> forwarding IP or Fully-Qualified Domain Name (FQDN)</li>
              <li><b>UDP Port</b> forwarding destination port</li>
              <li><b>UDP PSK</b> to verify identity using CoAP overhead</li>
            </ul>
          </dd>
        </dl>
      </div>
    </div>

    <div v-if="auth.isAdmin" class="row mb-3 align-items-center">
      <AddActions view="APN" @new="newModal"/>
      <div class="col d-flex justify-content-end">
        <ToggleLockedButton 
          v-model="includeLocked" 
          class="me-2"
          @toggle="toggleLockedFilter"
        />
      </div>
    </div>
    <div>
      <ApnForm v-if="modalNew || modalEdit || modalDetail" :tag="`apn`"
          :msg="`${modalMsg} APN`" :parentObj="editApn"
          :schema="entitySchema" :schemaTypes="schemaTypes"
          @formDataSubmitted="handleSave" @formClosed="closedModal"
      />
    </div>
    <div>
      <ApnTable 
        modelName="APN"
        :columns="columns" 
        :options="tableOptions"
        :ajaxUrl="apnsEndpoint" 
        :queryParams="apnQueryParams" 
        ref="table"
      >
        <template #niddStatus="props">
          <div v-if="props.rowData.type === 'NIDD'" class="text-center">
            <i v-if="isNiddReady(props.rowData)" 
              class="bi bi-check-circle-fill text-success" 
              v-tooltip title="All callbacks configured"></i>
            <i v-else 
              class="bi bi-exclamation-triangle-fill text-warning fs-5" 
              v-tooltip title="Missing callback configuration"></i>
          </div>
        </template>
      <template #details="row">
        <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
      </template>
      <template #actions="row">
        <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
      </template>
      </ApnTable>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint, logVerbose, udpProxyHost, udpProxyPort } from '@/utils/config';
import { getCommonColumns } from '@/utils/table';
import { handleRowSave, handleRowDelete } from '@/utils/form';

import ApnTable from '@/components/TableComponent.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import ApnForm from '@/components/DataFormModal.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';

// --- Stores & State ---
const auth = useAuthStore();
const schema = useSchemaStore();

const table = ref();
const apnList = ref([]);
const entitySchema = ref([]);
const schemaTypes = reactive({});
const includeLocked = ref(false);

const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);
const editApn = reactive({});

// --- Computed ---
const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const columns = [
  { title: 'APN', data: 'id', width: '15%' },
  { title: 'Parent APN', data: 'parentId', width: '15%', visible: auth.isAdmin },
  { title: 'Customer ID', data: 'customerId' },
  { title: 'Type', data: 'type', width: '10%' },
  { 
    title: 'NIDD Callbacks',
    tooltip: `NIDD readiness requires all 'Callback' fields to be populated`, 
    data: null, 
    render: '#niddStatus', 
    orderable: false, 
    searchable: false,
    width: '15%',
    className: 'text-center'
  },
  { title: 'UDP Server', data: 'updServer', defaultContent: '-', width: '15%' },
  ...getCommonColumns(auth.isAdmin),
];

const tableOptions = computed(() => {
  return {
    fixedColumns: { left: 1, right: auth.isAdmin ? 2 : 1 },
  };
});

const apnsEndpoint = ApiEndpoint.APNS;
const apnQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return params;
});

// --- Methods ---
const loadApns = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const toggleLockedFilter = async () => {
  await loadApns(true);
};

const isNiddReady = (row) => {
  // Find all keys starting with 'cb'
  const callbackKeys = Object.keys(row).filter(key => key.startsWith('cb'));
  for (const key of callbackKeys) {
    const value = row[key];
    if (value === null || value === '') {
      if (logVerbose) console.warn(`${key} not defined for ${row.id}`);
      return false;
    }
  }
  return true;
};

const resetApn = (obj) => {
  Object.keys(obj).forEach(key => delete obj[key]);
};

const newModal = () => {
  resetApn(editApn);
  modalNew.value = true;
};

const handleSave = async (detail) => {
  try {
    await handleRowSave('apn', detail, { ...editApn }, apnList);
    closedModal();
    await loadApns(true);
  } catch (err) {
    console.error(err);
  }
};

const edit = (row) => {
  Object.assign(editApn, row);
  modalEdit.value = true;
};

const detail = (row) => {
  Object.assign(editApn, row);
  modalDetail.value = true;
};

const remove = async (row) => {
  const validationKeys = ['id', 'customerId'];
  await handleRowDelete('apn', row, validationKeys);
  await loadApns(true);
};

const closedModal = () => {
  modalNew.value = modalEdit.value = modalDetail.value = false;
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('apn');
  schemaTypes.value = schema.getModelEnums('apn');
});
</script>

<style scoped>
.pointer { cursor: pointer; }
.action { margin-left: 8px; }
/* Ensure icon set are sized correctly */
.bi-check-circle-fill {
  font-size: 1.2rem;
}
.text-success {
  color: #198754 !important; /* Bootstrap Green */
}
</style>
